import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Box,
  Button,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import QRCode from "react-qr-code";
import VoiceWaveform from "./VoiceWaveform";
import { StyledButton } from "../StyledComponents";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import axios from "axios";
import AudioFileIcon from "@mui/icons-material/AudioFile";

const VoiceInputModal = ({
  openMicModal,
  handleCloseMicModal,
  language,
  setLanguage,
  sectionToEdit,
  setSectionToEdit,
  isRecording,
  handleStartRecording,
  handleStopRecording,
  streamRef,
  audioBlob,
  setAudioBlob,
  handleSubmitRecording,
  handleSubmitRecordingEdit,
  handleSubmitTextPrescription,
  isSubmittingTextPrescription = false,
  handlePauseRecording,
  handleResumeRecording,
  isPaused,
  voiceTranscripton,
  limitMessage,
  patientId,
  authToken,
  backendUrl,
  facilityName,
  transcriptionEnabled = true,
}) => {
  const [mode, setMode] = useState("add");
  const [voiceText, setVoiceText] = useState("");
  const [prescriptionText, setPrescriptionText] = useState("");
  const [timerKey, setTimerKey] = useState(0); // used to reset the timer
  const prevRecordingRef = useRef(isRecording);

  // QR Token state
  const [qrToken, setQrToken] = useState("");
  const [qrTokenLoading, setQrTokenLoading] = useState(false);
  const [qrTokenError, setQrTokenError] = useState("");
  const tokenTimerRef = useRef(null);

  const [voiceRecordings, setVoiceRecordings] = useState([]);
  const [voiceRecordingsLoading, setVoiceRecordingsLoading] = useState(false);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [selectedVoiceS3Key, setSelectedVoiceS3Key] = useState(null);

  const textEnabled = typeof handleSubmitTextPrescription === "function";

  const orgCode = sessionStorage.getItem("ORG_CODE") || "";
  const appMode = sessionStorage.getItem("APP_MODE") || "";
  const REACT_APP_BACKEND_ROUTE_URL =
    backendUrl || process.env.REACT_APP_BACKEND_ROUTE_URL;

  /** Matches Django check_user_access: App-Mode, Org-Code, Username, User-Access-Key */
  const getAuthHeaders = useCallback(() => {
    const token = authToken || sessionStorage.getItem("accessToken") || "";
    const mode =
      sessionStorage.getItem("APP_MODE") ||
      sessionStorage.getItem("appMode") ||
      "";
    const org =
      sessionStorage.getItem("ORG_CODE") ||
      sessionStorage.getItem("orgCode") ||
      "";
    const username =
      sessionStorage.getItem("userName") ||
      sessionStorage.getItem("username") ||
      "";
    const accessKey =
      sessionStorage.getItem("USER_ACCESS_KEY") ||
      sessionStorage.getItem("accessKey") ||
      "";
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "App-Mode": mode,
      "Org-Code": org,
      Username: username,
      "User-Access-Key": accessKey,
    };
  }, [authToken]);

  // Generate QR upload token
  const generateQrToken = useCallback(async () => {
    if (!patientId || !orgCode) return;

    setQrTokenLoading(true);
    setQrTokenError("");

    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}generate-upload-token/`,
        {
          patient_id: patientId,
          org_code: orgCode,
          app_mode: appMode,
          facility_name: facilityName || "",
        },
        { headers: getAuthHeaders() }
      );

      if (response.data?.status === "Success" && response.data?.token) {
        setQrToken(response.data.token);

        // Auto-refresh token 30 seconds before expiry (tokens last 10 min)
        clearTimeout(tokenTimerRef.current);
        tokenTimerRef.current = setTimeout(() => {
          generateQrToken();
        }, 9.5 * 60 * 1000); // 9.5 minutes
      } else {
        setQrTokenError("Failed to generate QR token.");
      }
    } catch (error) {
      console.error("QR token generation error:", error);
      setQrTokenError("Error generating QR code. Please try again.");
    } finally {
      setQrTokenLoading(false);
    }
  }, [
    patientId,
    orgCode,
    appMode,
    facilityName,
    REACT_APP_BACKEND_ROUTE_URL,
    getAuthHeaders,
  ]);

  const fetchVoiceRecordings = useCallback(async () => {
    if (!patientId) return;

    setVoiceRecordingsLoading(true);
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get-patient-uploaded-documents/`,
        {
          patient_id: patientId,
          upload_type: "voice",
          include_base64: true,
        },
        { headers: getAuthHeaders() }
      );

      if (response.data?.status === "Success") {
        setVoiceRecordings(response.data.documents || []);
      }
    } catch (error) {
      console.error("Error fetching voice recordings:", error);
    } finally {
      setVoiceRecordingsLoading(false);
    }
  }, [patientId, REACT_APP_BACKEND_ROUTE_URL, getAuthHeaders]);

  const blobFromBase64 = (base64, contentType) => {
    const clean = (base64 || "").replace(/\s/g, "");
    if (!clean) return null;
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    const mime =
      contentType && contentType !== "binary/octet-stream"
        ? contentType
        : "audio/webm";
    return new Blob([bytes], { type: mime });
  };

  const handlePickExistingVoice = async (rec) => {
    try {
      let blob = null;
      if (rec?.file_base64) {
        blob = blobFromBase64(rec.file_base64, rec.content_type);
      }
      if (!blob && rec?.url) {
        const resp = await fetch(rec.url);
        if (resp.ok) blob = await resp.blob();
      }
      if (!blob) {
        return;
      }
      if (typeof setAudioBlob === "function") {
        setAudioBlob(blob);
      }
      setSelectedVoiceS3Key(rec.s3_key || null);
    } catch (e) {
      console.error("Error selecting existing voice:", e);
    }
  };

  useEffect(() => {
    if (openMicModal) {
      generateQrToken();
      fetchVoiceRecordings();
      setPrescriptionText("");
    }

    return () => {
      clearTimeout(tokenTimerRef.current);
    };
  }, [openMicModal, generateQrToken, fetchVoiceRecordings]);

  useEffect(() => {
    if (!transcriptionEnabled && mode === "transcript") {
      setMode("add");
    }
    if (!textEnabled && mode === "text") {
      setMode("add");
    }
  }, [transcriptionEnabled, textEnabled, mode]);

  const qrUrl = qrToken
    ? `${window.location.origin}/upload_voice?token=${encodeURIComponent(qrToken)}`
    : "";

  useEffect(() => {
    if (voiceTranscripton) {
      setVoiceText((prev) => prev + voiceTranscripton + "\n");
    }
  }, [voiceTranscripton]);

  // Detect transitions of isRecording to reset/start/stop timer
  useEffect(() => {
    // start -> increment key to ensure fresh timer instance
    if (isRecording && !prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    // stop -> increment key to reset (and the timer will be hidden because isRecording=false)
    if (!isRecording && prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    prevRecordingRef.current = isRecording;
  }, [isRecording]);

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
    { code: "mix", name: "Mixed Language" },
  ];

  // Helper to format remaining seconds as MM:SS
  const formatMMSS = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const renderQrSection = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 160,
        p: 2,
        borderLeft: "1px solid #e0e0e0",
      }}
    >
      {qrTokenLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <CircularProgress size={40} />
          <Typography variant="caption" sx={{ color: "#666" }}>
            Generating QR...
          </Typography>
        </Box>
      ) : qrTokenError ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "#d32f2f", textAlign: "center" }}>
            {qrTokenError}
          </Typography>
          <IconButton size="small" onClick={generateQrToken} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      ) : qrUrl ? (
        <>
          <QRCode value={qrUrl} size={140} />
          <Typography
            variant="caption"
            sx={{ mt: 1, color: "#666", textAlign: "center" }}
          >
            Scan to upload voice
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#999", textAlign: "center", fontSize: "0.65rem" }}
          >
            Expires in 10 min
          </Typography>
        </>
      ) : (
        <Typography variant="caption" sx={{ color: "#999", textAlign: "center" }}>
          QR code unavailable
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={openMicModal}
      onClose={handleCloseMicModal}
      fullWidth
      maxWidth="md"
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "1.2rem",
          color: "#125386",
        }}
      >
        Input Settings
        <IconButton onClick={handleCloseMicModal}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Mode Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginLeft: 1,
          marginRight: 1,
        }}
      >
        <Tabs
          value={mode}
          onChange={(e, newValue) => {
            if (isRecording) return; // Block tab changes if recording is on
            setMode(newValue);
          }}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{
            style: { display: "none", boxShadow: "0 4px 4px 0 #C7C7CC inset" },
          }}
        >
          <Tab
            label="RECORD"
            value="add"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "add" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          <Tab
            label="EDIT"
            value="edit"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "edit" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          {textEnabled && (
          <Tab
            label="TEXT"
            value="text"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "text" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          )}
          {transcriptionEnabled && (
          <Tab
            label="TRANSCRIPT"
            value="transcript"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "transcript" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          )}
        </Tabs>
      </Box>

      {/* Content */}
      {transcriptionEnabled && mode === "transcript" ? (
        <DialogContent>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Transcription:
              </Typography>

              <Box
                sx={{
                  p: 2,
                  minHeight: "80px",
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "#f7f7f7",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {voiceText || "No transcription available."}
                </Typography>
              </Box>

              {/* Uploaded Voice Recordings */}
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Uploaded Voice Recordings
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={fetchVoiceRecordings}
                    disabled={voiceRecordingsLoading}
                    color="primary"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>

                {voiceRecordingsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : voiceRecordings.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {voiceRecordings.map((rec, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 1.5,
                          border: "1px solid #e2e8f0",
                          borderRadius: 2,
                          bgcolor: "#fff",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                          <AudioFileIcon
                            sx={{ color: "#3b82f6", mr: 1, fontSize: "1.2rem" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.85rem",
                              color: "#334155",
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rec.file_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", ml: 1 }}
                          >
                            {rec.upload_date}
                          </Typography>
                        </Box>
                        <audio
                          controls
                          src={rec.url}
                          style={{
                            width: "100%",
                            height: "36px",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "#94a3b8", fontStyle: "italic", py: 1 }}
                  >
                    No voice recordings uploaded by patient yet.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* QR Code */}
            {renderQrSection()}
          </Box>
        </DialogContent>
      ) : textEnabled && mode === "text" ? (
        <DialogContent sx={{ padding: "12px 24px" }}>
          <Box
            sx={{
              backgroundColor: "#FFF8E1",
              border: "1px solid #FFECB3",
              borderRadius: "6px",
              padding: "0px 12px",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              color: "#795548",
              fontSize: "0.875rem",
              lineHeight: 1.4,
              marginBottom: 1.5,
            }}
          >
            <span style={{ fontSize: "18px", flexShrink: 0 }}>⚠️</span>

            <Typography
              component="span"
              sx={{
                display: "inline",
                flex: 1,
                wordBreak: "break-word",
                fontSize: "0.8rem",
              }}
            >
              AI-generated content may contain transcription or interpretation
              errors. Please review and correct all details before submitting.
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{ display: "block", color: "#666", mb: 1 }}
          >
            Text input supports English only.
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={8}
            value={prescriptionText}
            onChange={(e) => setPrescriptionText(e.target.value)}
            placeholder="e.g. patient is having headache from 5 days"
            disabled={isSubmittingTextPrescription}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <StyledButton
              bgColor="#5DAA60"
              onClick={() => handleSubmitTextPrescription(prescriptionText)}
              disabled={
                !prescriptionText.trim() || isSubmittingTextPrescription
              }
              variant="contained"
              sx={{
                fontSize: { xs: "0.6rem", sm: "0.8rem" },
                minWidth: 120,
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {isSubmittingTextPrescription ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Submit"
              )}
            </StyledButton>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent sx={{ padding: "12px 24px" }}>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  backgroundColor: "#FFF8E1",
                  border: "1px solid #FFECB3",
                  borderRadius: "6px",
                  padding: "0px 12px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  color: "#795548",
                  fontSize: "0.875rem",
                  lineHeight: 1.4,
                  marginBottom: 1.5,
                }}
              >
                <span style={{ fontSize: "18px", flexShrink: 0 }}>⚠️</span>

                <Typography
                  component="span"
                  sx={{
                    display: "inline",
                    flex: 1,
                    wordBreak: "break-word",
                    fontSize: "0.8rem",
                  }}
                >
                  AI-generated content may contain transcription or interpretation
                  errors. Please review and correct all details before submitting.
                </Typography>
              </Box>
              {/* Language Dropdown */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={isRecording}
                  displayEmpty
                  sx={{
                    height: "60%",
                  }}
                  MenuProps={{
                    disablePortal: true,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { style: { maxHeight: 200, width: "auto" } },
                  }}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Section Dropdown for Edit Mode */}
              {mode === "edit" && (
                <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
                  <Select
                    multiple
                    value={sectionToEdit}
                    onChange={(e) => setSectionToEdit(e.target.value)}
                    disabled={isRecording}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return <>Select one or more sections</>;
                      }
                      return selected.join(", ");
                    }}
                    MenuProps={{
                      disablePortal: true,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                      PaperProps: { style: { maxHeight: 180, width: "auto" } },
                    }}
                  >
                    <MenuItem value="Chief Complaints">Chief Complaints</MenuItem>
                    <MenuItem value="Tests/Investigations">
                      Tests Advised
                    </MenuItem>
                    <MenuItem value="Diagnosis">Diagnosis</MenuItem>
                    <MenuItem value="Procedures">Procedures</MenuItem>
                    <MenuItem value="Medications">Medications</MenuItem>
                    <MenuItem value="FollowUp">Follow Up</MenuItem>
                    <MenuItem value="Referral">Referral</MenuItem>
                    <MenuItem value="VitalSigns">Examination/Vitals</MenuItem>
                    <MenuItem value="History of Past Illness">History of Past Illness</MenuItem>
                    <MenuItem value="History of Present Illness">History of Present Illness</MenuItem>
                    <MenuItem value="Family History">Family History</MenuItem>
                    <MenuItem value="Drug History">Drug History</MenuItem>
                    <MenuItem value="Allergies">Allergies</MenuItem>
                    <MenuItem value="Addiction History">Addiction History</MenuItem>
                    <MenuItem value="Surgeries">Surgeries</MenuItem>
                    <MenuItem value="Menstrual History">Menstrual History</MenuItem>
                  </Select>
                </FormControl>
              )}

              <Box sx={{ mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentPasteIcon />}
                  onClick={() => {
                    setVoicePickerOpen((prev) => !prev);
                    if (!voicePickerOpen) fetchVoiceRecordings();
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Choose existing voice
                </Button>
              </Box>

              {voicePickerOpen && (
                <Box
                  sx={{
                    mb: 2,
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: "#f8fafc",
                    maxHeight: 180,
                    overflowY: "auto",
                  }}
                >
                  {voiceRecordingsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : voiceRecordings.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      No voice recordings found.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {voiceRecordings.map((rec, idx) => {
                        const chosen = rec.s3_key === selectedVoiceS3Key;
                        return (
                          <Box
                            key={rec.s3_key || idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              p: 1,
                              border: "1px solid #e2e8f0",
                              borderRadius: 1.5,
                              bgcolor: "#fff",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "#334155",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 260,
                              }}
                            >
                              {rec.file_name}
                            </Typography>
                            <Button
                              size="small"
                              variant={chosen ? "contained" : "outlined"}
                              onClick={() => handlePickExistingVoice(rec)}
                              sx={{ textTransform: "none" }}
                            >
                              {chosen ? "Chosen" : "Select"}
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Buttons Row */}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Grid container justifyContent="space-between">
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={8}
                    display="flex"
                    justifyContent="flex-start"
                    gap={1}
                  >
                    <Grid item xs={6} md={6} lg={5}>
                      <StyledButton
                        bgColor="#0E3F66"
                        onClick={handleStartRecording}
                        disabled={isRecording}
                        variant="contained"
                        sx={{
                          fontSize: { xs: "0.6rem", sm: "0.8rem" },
                          width: "90%",
                          height: "90%",
                          "&:hover": { backgroundColor: "#07367dff" },
                        }}
                      >
                        Start Recording
                      </StyledButton>
                    </Grid>

                    {/* Pause/Resume Button */}
                    <Grid item xs={6} md={6} lg={4}>
                      <StyledButton
                        bgColor={isPaused ? "#5DAA60" : "#FFA500"}
                        onClick={
                          isPaused ? handleResumeRecording : handlePauseRecording
                        }
                        disabled={!isRecording}
                        variant="contained"
                        fullWidth
                        sx={{
                          fontSize: { xs: "0.6rem", sm: "0.8rem" },
                          width: "90%",
                          height: "90%",
                          "&:hover": {
                            backgroundColor: isPaused ? "#1b5e20" : "#FF8C00",
                          },
                        }}
                      >
                        {isPaused ? "Resume" : "Pause"}
                      </StyledButton>
                    </Grid>

                    <Grid item xs={6} md={6} lg={5}>
                      <StyledButton
                        bgColor="#FF3B30"
                        onClick={handleStopRecording}
                        disabled={!isRecording}
                        variant="contained"
                        sx={{
                          fontSize: { xs: "0.6rem", sm: "0.8rem" },
                          width: "90%",
                          height: "90%",
                          "&:hover": { backgroundColor: "#b71c1c" },
                        }}
                      >
                        Stop Recording
                      </StyledButton>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} md={6} lg={3}>
                    <StyledButton
                      bgColor="#5DAA60"
                      onClick={() => {
                        if (mode === "add") {
                          handleSubmitRecording();
                        } else {
                          handleSubmitRecordingEdit({ edit: "group" });
                        }
                      }}
                      disabled={
                        isRecording ||
                        (mode === "edit" && sectionToEdit.length === 0)
                      }
                      variant="contained"
                      sx={{
                        fontSize: { xs: "0.6rem", sm: "0.8rem" },
                        width: "90%",
                        height: "90%",
                        "&:hover": { backgroundColor: "#1b5e20" },
                      }}
                    >
                      Submit
                    </StyledButton>
                  </Grid>
                </Grid>
              </Box>

              {/* Waveform + Timer in same line */}
              {isRecording && streamRef.current && (
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <VoiceWaveform
                        stream={streamRef.current}
                        isRecording={isRecording}
                        isPaused={isPaused}
                      />
                    </Box>

                    <Box
                      sx={{
                        width: 110,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CountdownCircleTimer
                        key={timerKey}
                        isPlaying={isRecording && !isPaused}
                        duration={240}
                        size={90}
                        strokeWidth={8}
                        colors={["#5DAA60", "#55C1A1", "#00A3E0", "#0E3F66"]}
                        colorsTime={[240, 160, 80, 0]}
                        onComplete={() => {
                          return { shouldRepeat: false, delay: 0 };
                        }}
                      >
                        {({ remainingTime }) => (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.9rem", fontWeight: 700 }}
                          >
                            {formatMMSS(remainingTime)}
                          </Typography>
                        )}
                      </CountdownCircleTimer>
                    </Box>
                  </Box>
                </Box>
              )}

              {limitMessage && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  {limitMessage}
                </Typography>
              )}

              {/* Audio Preview */}
              {audioBlob && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Preview:
                  </Typography>
                  <audio controls src={URL.createObjectURL(audioBlob)} />
                </Box>
              )}
            </Box>

            {/* QR Code */}
            {renderQrSection()}
          </Box>
        </DialogContent>
      )}

      <DialogActions />
    </Dialog>
  );
};

export default VoiceInputModal;
